import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Scale, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export function ForgotPassword() {
  const { t } = useApp();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (er) { toast.error(formatError(er)); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell title="Ξεχάσατε τον κωδικό;" subtitle="Θα σας στείλουμε σύνδεσμο επαναφοράς στο email σας.">
      {sent ? (
        <div className="text-center" data-testid="forgot-sent">
          <p className="mb-4">Αν υπάρχει λογαριασμός με αυτό το email, στείλαμε σύνδεσμο επαναφοράς.</p>
          <Link to="/login" className="gold-text text-[13px] hover:underline">← Επιστροφή σε σύνδεση</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Email</label>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
              data-testid="forgot-email-input"
              className="mt-1.5 w-full bg-muted/30 border border-border/80 rounded-md px-3 py-2.5 text-[13.5px] outline-none focus:border-[hsl(var(--gold)/0.5)]"/>
          </div>
          <button type="submit" disabled={loading} data-testid="forgot-submit"
            className="w-full px-4 py-2.5 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[13.5px] disabled:opacity-60">
            {loading ? "Αποστολή..." : "Αποστολή συνδέσμου"}
          </button>
          <div className="text-center text-[12.5px]">
            <Link to="/login" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <ArrowLeft className="size-3.5"/>Επιστροφή σε σύνδεση
            </Link>
          </div>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Οι κωδικοί δεν ταιριάζουν"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Ο κωδικός ενημερώθηκε");
      navigate("/login");
    } catch (er) { toast.error(formatError(er)); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell title="Ορισμός νέου κωδικού" subtitle="Επιλέξτε έναν ισχυρό κωδικό.">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        {["Νέος κωδικός", "Επιβεβαίωση"].map((lbl, i) => (
          <div key={i}>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{lbl}</label>
            <input type="password" required minLength={6}
              data-testid={i === 0 ? "reset-password-input" : "reset-confirm-input"}
              value={i === 0 ? password : confirm}
              onChange={e => (i === 0 ? setPassword(e.target.value) : setConfirm(e.target.value))}
              className="mt-1.5 w-full bg-muted/30 border border-border/80 rounded-md px-3 py-2.5 text-[13.5px] outline-none focus:border-[hsl(var(--gold)/0.5)]"/>
          </div>
        ))}
        <button type="submit" disabled={loading || !token} data-testid="reset-submit"
          className="w-full px-4 py-2.5 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[13.5px] disabled:opacity-60">
          {loading ? "Αποθήκευση..." : "Αλλαγή κωδικού"}
        </button>
      </form>
    </AuthShell>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-8">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="size-11 rounded-lg flex items-center justify-center bg-[hsl(var(--gold)/0.15)] border gold-border">
            <Scale className="size-6 gold-text" strokeWidth={1.6}/>
          </div>
          <div>
            <div className="font-heading font-bold text-base leading-none">ΙΧΝΟΔΙΚΗΣ</div>
          </div>
        </div>
        <h2 className="font-display text-3xl font-semibold mb-2">{title}</h2>
        <p className="text-muted-foreground text-[13px] mb-6">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}
